import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useAnalytics} from '@github-ui/use-analytics'
import {fetchCAPI} from '../utils/fetch-capi'
import {environmentApiPath} from '../utils/api-url-helpers'
import {EnvironmentSchema, ENVIRONMENTS_MAX_AGE_MS} from './use-environments'
import type {Environment} from '../types/environment'

export const environmentQueryKey = ['agent-environment'] as const

const EnvironmentValidator = Compile(EnvironmentSchema)

interface UseEnvironmentOptions {
  enabled?: boolean
}

/**
 * Fetches a single AHP environment by id, resolving any kind — including the
 * per-session `managed-sandbox` allocations that {@link useEnvironments} omits.
 */
export function useEnvironment(environmentId: string | undefined, {enabled = true}: UseEnvironmentOptions = {}) {
  const flagEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const queryEnabled = flagEnabled && enabled && !!environmentId
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: EnvironmentValidator,
        sendAnalyticsEvent,
        prefix: 'copilot-agent-environment',
        maxAgeMs: ENVIRONMENTS_MAX_AGE_MS,
        enabled: queryEnabled,
      }),
    [sendAnalyticsEvent, queryEnabled],
  )

  return useQuery<Environment | null>({
    queryKey: [...environmentQueryKey, environmentId ?? null],
    enabled: queryEnabled,
    queryFn: async () => {
      if (!environmentId) return null
      try {
        const response = await fetchCAPI({path: environmentApiPath(environmentId)})
        const data = await response.json()
        if (!data || typeof data !== 'object' || Array.isArray(data)) return null
        // Strip `webpubsub` (carries an `access_token`) so it never hits the cache.
        const {webpubsub: _webpubsub, ...environment} = data as Environment
        return environment
      } catch {
        return null
      }
    },
    persister,
  })
}
